import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
const { Panel: DialogPanel, Title: DialogTitle } = Dialog;
const { Child: TransitionChild } = Transition;
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { useTerminateTrade } from '@/hooks/trades';
import { useToastStore } from '@/stores';
import { getErrorMessage } from '@/utils/get-error-message';
import { formatDecimalString, toDecimalString } from '@/utils/decimal-display';
import { formatDate, todayLocalKey } from '@/utils/format';
// Import canPerform from its specific module, NOT the @/features/trades/hooks
// barrel — the barrel pulls the whole hooks tree (useTradeActions → inventory
// stores), which over-mocking-sensitive modal unit tests can't satisfy.
import { canPerform } from '@/features/trades/hooks/trade-actions';
import { getEffectiveTradeStatusLabel } from '@/utils/trades/effective-status';
import type { PositionDetail } from '@/types';

interface TerminateModalProps {
  isOpen: boolean;
  onClose: () => void;
  trade: PositionDetail;
  /**
   * The `useTerminateTrade` mutation handle composed by the page-level
   * `useTradeActions` (LR-208 r3 M4). The page is the single mutation owner;
   * the modal no longer self-hosts a second subscription.
   */
  terminate: ReturnType<typeof useTerminateTrade>;
}

const TERMINATION_REASONS = [
  { value: 'MATURITY', label: 'Maturity' },
  { value: 'EARLY_RECALL', label: 'Early Recall' },
  { value: 'EARLY_RETURN', label: 'Early Return' },
  { value: 'MARGIN_DEFICIENCY', label: 'Margin Deficiency' },
  { value: 'REGULATORY', label: 'Regulatory' },
];

const REJECTED_CANCEL_REASONS = [
  { value: 'BO_ACCOUNT_MISSING', label: 'BO account never created' },
  { value: 'BO_REF_DATA', label: 'BO reference-data broken' },
  { value: 'STRATEGY_ABANDONED', label: 'Strategy abandoned' },
  { value: 'OTHER', label: 'Other' },
];

export function TerminateModal({ isOpen, onClose, trade, terminate }: TerminateModalProps) {
  const [reason, setReason] = useState('');
  // LR-208 r5 M1: local-zone day key; pre-r5 the UTC slice silently emitted
  // "tomorrow UTC" as the default termination date from a UTC-negative evening.
  const [terminationDate, setTerminationDate] = useState(todayLocalKey());
  const [comment, setComment] = useState('');
  const { mutate, isPending } = terminate;
  const { addToast } = useToastStore();

  // LR-208 r5 M2: a rejected trade never opened in BO — backend's terminate()
  // ignores actual_settlement_date and just flips status to cancelled. Surface
  // that distinction in the UI so the trader does not believe they are
  // scheduling a settlement (pre-r5 the form showed a required Termination
  // Date input the wire silently discarded — same class of fiction-UI as
  // r3 H3 NoticeModal).
  const isRejectedCancel = trade.status === 'rejected';

  // LR-695: Terminate fixes the day the loan ENDS. A termination dated today
  // matures the trade at once (the pre-LR-695 behaviour, now the T+0 case); a
  // later date becomes the Maturity Date and the close date together, and the
  // trade stays active — accruing — until then. The preview and the success
  // toast both say which of the two the trader is about to get. Compared on
  // the local day key, like the input's own floor.
  const endsToday = terminationDate === todayLocalKey();
  const maturityMoves =
    !!trade.maturity_date && !!terminationDate && trade.maturity_date !== terminationDate;
  // A Term maturity, or an Evergreen maturity after notice, is a contractual end
  // Terminate may only bring forward — the backend refuses a later date, so the
  // input stops offering one. Not applied once that maturity is in the past (the
  // maturity run has not flipped the row yet): the backend then bounds at today.
  const contractualEnd =
    (trade.term_type === 'term' ||
      (trade.term_type === 'evergreen' && trade.notice_served === true)) &&
    trade.maturity_date &&
    trade.maturity_date >= todayLocalKey()
      ? trade.maturity_date
      : undefined;

  // The confirm card's "Required Collateral" = `contract_value` (backend-
  // authoritative: qty × gross_price × margin_decimal). Authoritative Decimal
  // STRING, formatted via the no-float helper — no parseFloat × quantity
  // (HON-03), no hardcoded `$`.
  // LR-474: settlement-primary — cross-ccy trades show the backend-converted
  // settlement figure (`contract_value_converted`); same-ccy falls back native.
  // LR-436 D5: relabelled "Collateral" → "Required Collateral" so it names the
  // same `contract_value` that AdequacyCard / analytics already call "Required
  // collateral" — disambiguating it from the genuinely-distinct posted
  // `collateral_value`. The tooltip carries the native security-ccy original so
  // the converted figure reconciles on hover.
  const isConvertedCollateral = toDecimalString(trade.contract_value_converted) != null;
  const collateralDisplay = isConvertedCollateral
    ? formatDecimalString(trade.contract_value_converted, {
        currency: trade.settlement_currency || undefined,
        dp: 2,
      })
    : formatDecimalString(trade.contract_value, {
        currency: trade.currency,
        dp: 2,
      });
  const collateralTitle = isConvertedCollateral
    ? `${formatDecimalString(trade.contract_value, { currency: trade.currency, dp: 2 })} in the security currency, converted at the trade FX rate`
    : undefined;

  const handleSubmit = () => {
    // Submit-path guard (ACTN-02 / "guard the full action path", LR-208 r6 LOW):
    // re-check validity against the freshly-loaded trade before mutating so a
    // stale/keyboard/race submit no-ops — mirrors SettlementDateModal.
    if (!canPerform(trade, 'terminate')) return;
    // For the active/normal path the termination date is required; a cleared
    // native date input must not silently submit as backend-today (LR-208 r6 LOW).
    if (!isRejectedCancel && !terminationDate) return;
    // LR-208 r5 L5: drop the trailing ": " when comment is empty so the audit
    // notes don't carry a dangling colon ("EARLY_RECALL: ") for every blank
    // submission. Keep the colon delimiter when both pieces are present.
    const composedReason = comment.trim()
      ? `${reason}: ${comment.trim()}`
      : reason;
    // The rejected branch on the BE ignores actual_settlement_date — sending
    // it is harmless (server discards it) but conceptually wrong, so omit it.
    const payload = isRejectedCancel
      ? { id: trade.id, reason: composedReason }
      : { id: trade.id, actualSettlementDate: terminationDate, reason: composedReason };
    mutate(payload, {
      onSuccess: (updated) => {
        // LR-695: the row stays Active after a future-dated Terminate — a bare
        // "terminated successfully" next to an Active badge reads as a failure
        // and invites a second click. Say what actually happened.
        const scheduled = !isRejectedCancel && updated?.status === 'active';
        addToast(
          'success',
          isRejectedCancel
            ? 'Rejected trade cancelled'
            : scheduled
              ? `Termination scheduled: the trade ends on ${formatDate(terminationDate)} and stays active until then`
              : 'Trade terminated successfully',
        );
        onClose();
      },
      onError: (error) => {
        addToast(
          'error',
          getErrorMessage(
            error,
            isRejectedCancel ? 'Failed to cancel trade' : 'Failed to terminate trade',
          ),
        );
      },
    });
  };

  const reasonOptions = isRejectedCancel ? REJECTED_CANCEL_REASONS : TERMINATION_REASONS;
  const dialogTitle = isRejectedCancel ? 'Cancel Rejected Trade' : 'Terminate Trade';
  const reasonLabel = isRejectedCancel ? 'Cancellation Reason *' : 'Termination Reason *';
  const cta = isRejectedCancel
    ? (isPending ? 'Cancelling...' : 'Cancel Trade')
    : (isPending ? 'Terminating...' : 'Terminate Trade');

  return (
    <Transition show={isOpen}>
      <Dialog onClose={onClose} className="relative z-[100]">
        <TransitionChild
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="w-full max-w-md bg-white rounded-xl shadow-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <DialogTitle className="text-lg font-semibold text-gray-900">{dialogTitle}</DialogTitle>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
              </div>

              {/* Trade info */}
              <div className="bg-red-50 rounded-lg p-3 mb-4 text-sm">
                <p className="text-red-800">Outstanding: <strong>{trade.quantity.toLocaleString()} {trade.symbol_id}</strong></p>
                <p className="text-red-800">
                  Required Collateral:{' '}
                  <strong className={collateralTitle ? 'lr-num cursor-help' : 'lr-num'} title={collateralTitle}>
                    {collateralDisplay}
                  </strong>
                </p>
              </div>

              {/* LR-478 S3: after-state preview for the normal terminate path.
                  The rejected path has its own note below (→ cancelled).
                  LR-695: two previews — today matures the trade now; a later
                  date fixes the end and leaves it active until then. */}
              {!isRejectedCancel && (
                <div
                  role="note"
                  aria-live="polite"
                  className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700"
                >
                  {endsToday ? (
                    <>
                      On confirm this position moves{' '}
                      <strong className="text-gray-900">{getEffectiveTradeStatusLabel(trade)}</strong>{' '}
                      → <strong className="text-gray-900">Matured</strong> today · closure runs on{' '}
                      {/* Humanize for the preview (LR-478 review r3); the native
                          date <input> below keeps the raw ISO value. */}
                      <strong className="text-gray-900">{formatDate(terminationDate)}</strong>{' '}
                      via the close-settlement run.
                    </>
                  ) : terminationDate ? (
                    <>
                      On confirm the loan ends on{' '}
                      <strong className="text-gray-900">{formatDate(terminationDate)}</strong>: that
                      becomes the Maturity Date
                      {maturityMoves && <> (moved from {formatDate(trade.maturity_date)})</>} and the
                      close date. The position stays{' '}
                      <strong className="text-gray-900">{getEffectiveTradeStatusLabel(trade)}</strong>,
                      accruing, until then — it matures and closes that day.
                    </>
                  ) : (
                    <>
                      Pick the date the loan ends. The position stays{' '}
                      <strong className="text-gray-900">{getEffectiveTradeStatusLabel(trade)}</strong>{' '}
                      and accrues until then.
                    </>
                  )}
                </div>
              )}

              {isRejectedCancel && (
                <div
                  role="note"
                  className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                >
                  This trade was rejected by Back Office before it opened, so there is no
                  settlement to schedule. Cancellation is immediate — the trade moves to
                  <code className="mx-1 rounded bg-amber-100 px-1 font-mono">cancelled</code>
                  and no BO unwind is required.
                </div>
              )}

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{reasonLabel}</label>
                  <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-red-500 focus:border-red-500">
                    <option value="">Select reason...</option>
                    {reasonOptions.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                {/* Termination Date — only rendered for non-rejected trades. The BE
                    rejected branch discards this field, so showing it would be
                    fiction-UI (LR-208 r5 M2). */}
                {!isRejectedCancel && (
                  <div>
                    <label
                      htmlFor="termination_date"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Termination Date *
                    </label>
                    <input
                      type="date"
                      id="termination_date"
                      value={terminationDate}
                      min={todayLocalKey()}
                      max={contractualEnd}
                      onChange={(e) => setTerminationDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Comment</label>
                  <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Optional comment..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-red-500 focus:border-red-500 resize-none" />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button onClick={handleSubmit} disabled={!reason || isPending} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40">
                  {cta}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
